import type { ReactNode } from 'react';

import styles from './review.module.scss';

const STAR_COUNT = 5;

export type ReviewProps = {
  reviewerName: string;
  reviewerInitials?: string;
  reviewerSubtitle?: string;
  reviewDateLabel?: string;
  stars?: number;
  ratingValue?: string;
  title?: string;
  body: string;
  reply?: string;
  replyLabel?: string;
  avatarUrl?: string;
  isVerified?: boolean;
  verifiedLabel?: string;
  hasReference?: boolean;
  referenceLabel?: string;
  truncated?: boolean;
  footer?: ReactNode;
  className?: string;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const getInitials = (name: string) => {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((segment) => segment.charAt(0).toUpperCase())
    .join('');
};

const Star = ({ filled }: { filled: boolean }) => (
  <svg
    className={filled ? styles.starFilled : styles.star}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 2.75l2.9 5.88 6.49.94-4.7 4.58 1.11 6.48-5.8-3.05-5.8 3.05 1.11-6.48-4.7-4.58 6.49-.94L12 2.75z" />
  </svg>
);

export const Review = ({
  reviewerName,
  reviewerInitials,
  reviewerSubtitle,
  reviewDateLabel,
  stars = 5,
  ratingValue,
  title,
  body,
  reply,
  replyLabel = 'Your reply:',
  avatarUrl,
  isVerified,
  verifiedLabel = 'Verified User',
  hasReference,
  referenceLabel = 'Reference Available',
  truncated = true,
  footer,
  className
}: ReviewProps) => {
  const normalizedStars = clamp(Math.round(stars), 0, STAR_COUNT);

  const cardClass = [styles.card, className].filter(Boolean).join(' ');
  const bodyClass = [styles.bodyText, !truncated && styles.bodyTextExpanded]
    .filter(Boolean)
    .join(' ');
  const replyTextClass = [
    styles.replyText,
    !truncated && styles.replyTextExpanded
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={cardClass}>
      <header className={styles.header}>
        <span className={styles.avatar} aria-hidden="true">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={reviewerName ? `Photo of ${reviewerName}` : 'Reviewer photo'}
              loading="lazy"
            />
          ) : (
            <span className={styles.initials}>
              {reviewerInitials ?? getInitials(reviewerName)}
            </span>
          )}
        </span>
        <div className={styles.headerMeta}>
          <div className={styles.headerRow}>
            <p className={styles.reviewerName}>{reviewerName}</p>
            {reviewerSubtitle && (
              <p className={styles.subtitle}>{reviewerSubtitle}</p>
            )}
            {reviewDateLabel && (
              <p className={styles.dateLabel}>{reviewDateLabel}</p>
            )}
          </div>
          {isVerified && (
            <div className={styles.badge}>
              <svg
                viewBox="0 0 266.66666 266.66666"
                aria-hidden="true"
                focusable="false"
              >
                <g transform="matrix(1.3333333,0,0,-1.3333333,0,266.66666)">
                  <g transform="scale(0.1)">
                    <path
                      d="M 2000,0 H 0 V 2000 H 2000 V 0"
                      style={{ fill: 'none', stroke: 'none' }}
                    />
                    <path
                      d="m 1047.51,1751.24 96.18,94.9 c 11.96,11.81 31.93,8.64 39.65,-6.28 l 62.15,-119.98 c 6.28,-12.11 21.11,-16.93 33.31,-10.82 l 120.8,60.54 c 15.02,7.53 33.04,-1.65 35.77,-18.23 l 22.03,-133.31 c 2.23,-13.45 14.85,-22.63 28.33,-20.58 l 133.61,20.24 c 16.6,2.51 30.89,-11.78 28.37,-28.39 l -20.24,-133.59 c -2.04,-13.49 7.13,-26.11 20.59,-28.34 l 133.32,-22.03 c 16.57,-2.74 25.75,-20.75 18.22,-35.76 l -60.54,-120.81 c -6.11,-12.19 -1.29,-27.03 10.82,-33.31 l 119.98,-62.15 c 14.93,-7.73 18.08,-27.69 6.28,-39.65 l -94.9,-96.18 c -9.58,-9.71 -9.58,-25.311 0,-35.022 l 94.9,-96.179 c 11.8,-11.957 8.65,-31.93 -6.28,-39.649 l -119.98,-62.148 c -12.11,-6.282 -16.93,-21.114 -10.82,-33.313 l 60.54,-120.801 c 7.53,-15.019 -1.65,-33.039 -18.22,-35.769 l -133.32,-22.027 c -13.46,-2.231 -22.63,-14.852 -20.59,-28.332 l 20.24,-133.61 c 2.52,-16.601 -11.77,-30.89 -28.37,-28.371 l -133.61,20.242 c -13.48,2.039 -26.1,-7.133 -28.33,-20.59 l -22.03,-133.32 c -2.73,-16.57 -20.75,-25.75 -35.77,-18.223 l -120.8,60.543 c -12.2,6.11 -27.03,1.289 -33.31,-10.82 l -62.15,-119.98 c -7.72,-14.93 -27.69,-18.082 -39.65,-6.282 l -96.18,94.903 c -9.71,9.578 -25.31,9.578 -35.02,0 l -96.181,-94.903 c -11.957,-11.8 -31.922,-8.648 -39.653,6.282 l -62.148,119.98 c -6.278,12.109 -21.113,16.93 -33.309,10.82 L 660.395,230.398 c -15.012,-7.527 -33.028,1.653 -35.766,18.223 l -22.031,133.32 c -2.223,13.457 -14.848,22.629 -28.336,20.59 L 440.668,382.289 c -16.609,-2.519 -30.902,11.77 -28.387,28.371 l 20.242,133.61 c 2.047,13.48 -7.128,26.101 -20.585,28.332 l -133.309,22.027 c -16.574,2.73 -25.754,20.75 -18.231,35.769 l 60.54,120.801 c 6.113,12.199 1.289,27.031 -10.821,33.313 l -119.98,62.148 c -14.918,7.719 -18.082,27.692 -6.282,39.649 l 94.907,96.179 c 9.578,9.711 9.578,25.312 0,35.022 l -94.907,96.18 c -11.8,11.96 -8.636,31.92 6.282,39.65 l 119.98,62.15 c 12.11,6.28 16.934,21.12 10.821,33.31 l -60.54,120.81 c -7.523,15.01 1.657,33.02 18.231,35.76 l 133.309,22.03 c 13.457,2.23 22.632,14.85 20.585,28.34 l -20.242,133.59 c -2.515,16.61 11.778,30.9 28.387,28.39 l 133.594,-20.24 c 13.488,-2.05 26.113,7.13 28.336,20.58 l 22.031,133.31 c 2.738,16.58 20.754,25.76 35.766,18.23 l 120.804,-60.54 c 12.196,-6.11 27.031,-1.29 33.309,10.82 l 62.148,119.98 c 7.731,14.92 27.696,18.09 39.653,6.28 l 96.181,-94.9 c 9.71,-9.58 25.31,-9.58 35.02,0"
                      style={{
                        fill: 'var(--color-brand-primary-500)',
                        fillOpacity: 1,
                        fillRule: 'nonzero',
                        stroke: 'none'
                      }}
                    />
                    <path
                      d="m 1348.55,1271.07 c -30.29,26.83 -77.03,23.99 -103.85,-6.3 L 915.273,892.84 810.199,997.922 c -25.879,25.878 -68.226,25.878 -94.109,0 l -9.934,-9.934 c -25.879,-25.886 -25.879,-68.226 0,-94.109 L 870.258,729.781 c 24.551,-24.562 63.762,-25.519 89.836,-3.492 5.648,3.582 11.035,7.691 15.676,12.941 l 379.07,427.99 c 26.83,30.29 24,77.02 -6.29,103.85"
                      style={{
                        fill: '#ffffff',
                        fillOpacity: 1,
                        fillRule: 'nonzero',
                        stroke: 'none'
                      }}
                    />
                  </g>
                </g>
              </svg>
              <span>{verifiedLabel}</span>
            </div>
          )}
          {hasReference && (
            <div className={styles.badge}>
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="M3 2.25h10c.69 0 1.25.56 1.25 1.25v9c0 .69-.56 1.25-1.25 1.25H3c-.69 0-1.25-.56-1.25-1.25v-9c0-.69.56-1.25 1.25-1.25zm0 1.5v9h10v-9H3zm6.22 2.03l1.53 1.53-4.22 4.22-2.28-2.28 1.53-1.53.75.74 2.96-2.96z" />
              </svg>
              <span>{referenceLabel}</span>
            </div>
          )}
        </div>
      </header>
      <div className={styles.body}>
        <div className={styles.starsRow}>
          <div
            className={styles.stars}
            aria-label={`${normalizedStars} out of ${STAR_COUNT} stars`}
            role="img"
          >
            {Array.from({ length: STAR_COUNT }, (_, index) => (
              <Star filled={index < normalizedStars} key={`star-${index}`} />
            ))}
          </div>
          {ratingValue && (
            <span className={styles.ratingValue}>{ratingValue}</span>
          )}
        </div>
        {title && <p className={styles.title}>{title}</p>}
        <p className={bodyClass}>{body}</p>
        {reply && (
          <div className={styles.reply}>
            <div className={styles.replyBar} aria-hidden="true" />
            <div className={styles.replyContent}>
              <p className={styles.replyLabel}>{replyLabel}</p>
              <p className={replyTextClass}>{reply}</p>
            </div>
          </div>
        )}
      </div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </article>
  );
};
